import { defineStore } from "pinia";
import cloneDeep from "lodash.clonedeep";
import { useAuthState } from "../states/authStates";
import type {
  AuthResponseDto,
  LoggedInDto,
  WebAccountDto,
  PersonDto,
  UserTypeDto,
  AddressDto,
  UserTypeParentDto,
} from "../types";
import { computed, useWebAccountsResource, $authResource, ref, useAuthResource, useNuxtApp } from "#imports";

/**
 * Authentication store. Contains data and methods for managing authentication state
 */
export const useAuthStore = defineStore( "auth", () => {
  const webAccountResource = useWebAccountsResource();
  const authResource = $authResource();
  const checkAuthResource = useAuthResource();
  const authState = useAuthState();
  const nuxtApp = useNuxtApp();

  // Get the authentication data from the state
  const authData = ref<LoggedInDto | null>( authState.value );

  // Shortcuts to commonly used web account data
  const webAccount = computed<WebAccountDto | undefined>( () => authData.value?.webAccount );
  const isLoggedIn = computed<boolean>( () => authData.value?.loggedIn || false );
  const webAccountId = computed<string | undefined>( () => webAccount.value?.webAccountId );
  const webUserTypeId = computed<number | undefined>( () => webAccount.value?.webUserTypeId );
  const webUserTypeParentId = computed<number | undefined>( () => webAccount.value?.webUserTypeParentId );
  const emailAddress = computed<string | undefined>( () => webAccount.value?.emailAddress );

  // Are children potentially available to switch to?
  const hasChildren = computed<boolean>( () => {
    if ( webAccount.value?.parentWebAccountId ) {
      return true;
    }

    return ( webAccount.value?.children?.length || 0 ) > 0;
  } );

  // Cache of the user's web user type
  const userType = ref<UserTypeDto | undefined>( undefined );
  const userTypeParent = ref<UserTypeParentDto | undefined>( undefined );

  // The address from the web account
  const accountAddress = computed<AddressDto | undefined>( () => webAccount.value?.address );

  // Person details from the web account
  const accountPerson = computed<PersonDto | undefined>( () => {
    if ( !webAccount.value ) {
      return undefined;
    }

    const result: PersonDto = {
      id: webAccount.value.personId,
      title: webAccount.value.title,
      firstName: webAccount.value.firstName,
      lastName: webAccount.value.lastName,
      email: webAccount.value.emailAddress,
      telephone: webAccount.value.telephone,
      jobTypeId: webAccount.value.jobTypeId,
    };

    return result;
  } );

  // The screen name for the currently logged-in user
  const screenName = computed<string | undefined | null>( () => {
    let result = null;

    if ( isLoggedIn.value ) {
      result = webAccount.value?.screenName;

      if ( !result ) {
        result = `${webAccount.value?.firstName || ""} ${webAccount.value?.lastName || ""}`;
      }

      if ( !result ) {
        result = webAccount.value?.username || webAccount.value?.emailAddress;
      }
    }

    return result?.trim();
  } );

  /**
   * Refresh the logged-in web account and other authentication details from the API
   */
  async function refreshAuthDetails () {
    const response = await webAccountResource.loggedInWebAccount();
    authData.value = response.data?.value;

    if ( authData.value?.webAccount?.webUserTypeId !== userType.value?.webAccountUserTypeId ) {
      // We need to make sure the user type cache is cleared if it doesn't match the current user
      userType.value = undefined;
    }
  }

  /**
   * Clear the store
   */
  function clearStore () {
    userType.value = undefined;

    authData.value = {
      loggedIn: false,
      webAccount: undefined,
    };
  }

  /**
   * Check to see if we are still logged in
   * If we are not logged in the clear the store
   */
  async function checkLogin (): Promise<boolean> {
    const response = await checkAuthResource.isLoggedIn( webAccountId.value );
    const loggedInData = response.data.value;
    const isLoggedIn = response.data.value?.loggedIn || false;

    if ( !isLoggedIn ) {
      // We are not logged in the server side
      clearStore();
      return false;
    }

    if ( loggedInData.webAccount ) {
      // We have been sent new web account data, so we are logged
      // in, but the client and server accounts don't match
      authData.value = loggedInData;
    }

    return true;
  }

  /**
   * Log the user out and clear the state
   */
  async function logOut () {
    const response = await authResource.logOut();
    if ( response ) {
      clearStore();

      // Call a custom Nuxt hook to signal we have logged in
      nuxtApp.callHook( "apollo:auth:signout" ).catch( () => {} );
    }
  }

  /**
   * DO a client side log in
   * @param details The authentication details
   */
  function clientLogin ( details: LoggedInDto | null ) {
    authData.value = details;
    userType.value = undefined;

    if ( details?.loggedIn ) {
      // Call a custom Nuxt hook to signal we have logged in
      nuxtApp.callHook( "apollo:auth:signin", authData.value?.webAccount ).catch( () => {
      } );
    }
  }

  /**
   * Log the user in and set the state data
   */
  async function logIn ( username: string, password: string, address?: AddressDto ): Promise<AuthResponseDto> {
    const response = await authResource.logIn( {
      username,
      password,
      address,
    } );

    if ( response?.loggedIn ) {
      // Log out client side
      clearStore();
      nuxtApp.callHook( "apollo:auth:signout" ).catch( () => {} );

      // Refresh store with new account details
      await refreshAuthDetails();

      // Call a custom Nuxt hook to signal we have logged in
      nuxtApp.callHook( "apollo:auth:signin", authData.value?.webAccount ).catch( () => {} );
    }

    return response;
  }

  /**
   * Get children for the current web account ID
   */
  async function getChildren (): Promise<WebAccountDto[]> {
    if ( !isLoggedIn ) {
      return [];
    }

    const response = await webAccountResource.getChildren();
    return response.data?.value;
  }

  /**
   * Log out the current account and switch to a new account (must be currently logged in)
   * @param webAccountId The ID of the web account to switch to
   */
  async function switchAccount ( webAccountId: string ): Promise<AuthResponseDto> {
    if ( !isLoggedIn.value ) {
      return {
        loggedIn: false,
      };
    }

    const response = await authResource.switchAccount( webAccountId );

    if ( response?.loggedIn ) {
      // We are logged in, get the old account
      const oldAccount = cloneDeep( authData.value?.webAccount ) || undefined;

      // Update the web account details
      await refreshAuthDetails();

      // Get the new account
      const newAccount = cloneDeep( authData.value?.webAccount ) || undefined;

      // Call a custom Nuxt hook to signal we have switched accounts
      nuxtApp.callHook( "apollo:auth:switch", oldAccount, newAccount ).catch( () => {} );
    }

    return response;
  }

  /**
   * Get the user type for the currently logged-in user from the store
   */
  async function getUserType (): Promise<UserTypeDto | undefined> {
    if ( userType.value ) {
      return userType.value;
    }

    const response = await webAccountResource.loggedInUserType();
    userType.value = response?.data.value;

    return userType.value;
  }

  /**
   * Get the user type parent for the currently logged-in user from the store
   */
  async function getUserTypeParent (): Promise<UserTypeParentDto | undefined> {
    if ( userTypeParent.value ) {
      return userTypeParent.value;
    }

    const response = await webAccountResource.loggedInUserTypeParent();
    userTypeParent.value = response?.data.value;

    return userType.value;
  }

  return {
    webAccount,
    isLoggedIn,
    screenName,
    webAccountId,
    webUserTypeId,
    webUserTypeParentId,
    emailAddress,
    accountPerson,
    accountAddress,
    refreshAuthDetails,
    clientLogin,
    logOut,
    logIn,
    checkLogin,
    clearStore,
    switchAccount,
    getUserType,
    getUserTypeParent,
    getChildren,
    hasChildren,
  };
} );
